import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, createBlock as _createBlock, createElementVNode as _createElementVNode, renderList as _renderList, vShow as _vShow, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_2 = {
  key: 0,
  class: "max-w-[1040px] flex flex-col gap-6"
}
const _hoisted_3 = { class: "grid grid-cols-2 lg:grid-cols-3 gap-4" }
const _hoisted_4 = { class: "text-sm text-gray-500" }
const _hoisted_5 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_6 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_7 = { class: "text-2xl font-semibold text-gray-900" }
const _hoisted_8 = { class: "flex items-center justify-between mb-3" }
const _hoisted_9 = { class: "flex-1 min-w-0 truncate" }
const _hoisted_10 = { class: "h-72" }
const _hoisted_11 = { "data-test": "customers-panel" }
const _hoisted_12 = {
  key: 1,
  class: "divide-y"
}
const _hoisted_13 = { class: "font-medium text-sm" }
const _hoisted_14 = { class: "text-xs text-gray-500" }
const _hoisted_15 = { class: "ml-1" }
const _hoisted_16 = {
  key: 2,
  class: "mt-4 flex items-center justify-between"
}
const _hoisted_17 = { class: "text-sm text-gray-500" }
const _hoisted_18 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_19 = { class: "bg-white rounded-lg shadow-xl w-full max-w-md p-6" }
const _hoisted_20 = {
  key: 0,
  class: "text-sm text-red-600 mt-2"
}
const _hoisted_21 = { class: "mt-5 flex justify-end gap-2" }
const _hoisted_22 = { class: "bg-white rounded-lg shadow-xl w-full max-w-md p-6" }
const _hoisted_23 = { class: "text-sm text-gray-600" }
const _hoisted_24 = { class: "mt-5 flex justify-end gap-2" }

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { CalendarDaysIcon } from '@heroicons/vue/24/outline'
import { DatePicker } from 'v-calendar'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useStore } from 'vuex'

import BarChart from '@/components/BarChart.vue'
import EmptyState from '@/components/neue/EmptyState.vue'
import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueToggle from '@/components/neue/NeueToggle.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import NewReportModal from '@/components/neue/NewReportModal.vue'
import ReportDownloadHistory from '@/components/reports/ReportDownloadHistory.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import SecondaryTop from '@/components/SecondaryTop.vue'
import { usePhoneInput } from '@/composables/usePhoneInput'
import { useRouteTabs } from '@/composables/useRouteTabs'
import { resellerName } from '@/config/nav/resellers'
import { dateObjectToServerDay, serverDayToDateObject } from '@/helpers/dates'
import { hasInvoicing } from '@/helpers/invoicing'
import api from '@/store/api'
import ResellerMembers from '@/view/resellers/ResellerMembers.vue'
import ResellerOrdersTable from '@/view/resellers/ResellerOrdersTable.vue'

// Same single series color as ResellerList.vue so cart value keeps one identity
// between the overview chart and this reseller's chart.
const SERIES_CART_VALUE = '#2a78d6'
const GRIDLINE = '#e5e7eb'
const AXIS_INK = '#6b7280'

const pageSize = 25

export default {
  __name: 'ResellerDetail',
  setup(__props) {

const route = useRoute()
const store = useStore()
const resellerId = ref(route.params.id)
const isCurrent = id => id === resellerId.value
const canInvoice = computed(() => hasInvoicing(store.state.loggedInUser))

const loading = ref(true)
const toggling = ref(false)
const reseller = ref(null)
const customers = ref([])
const reportRows = ref([])

const customersPage = ref(0)
const customersTotal = ref(0)
// One paged list per tab: placed orders (paid, dated by payment) and unplaced
// orders (unpaid carts, dated by creation). `status` is the bottles endpoint param.
const newOrdersList = status => reactive({ status, rows: [], page: 0, total: 0, pageSize })
const placedOrders = newOrdersList('placed')
const unplacedOrders = newOrdersList('unplaced')
const customersPageCount = computed(() => Math.max(1, Math.ceil(customersTotal.value / pageSize)))
const removeTarget = ref(null)
const removing = ref(false)

// BOT-1610: team members and reports are tabs of their own.
const resellerTabs = computed(() => [
  { id: 'customers', name: `Customers (${customersTotal.value})` },
  { id: 'team-members', name: `Team members (${(reseller.value?.attributes.members || []).length})` },
  { id: 'orders', name: `Orders (${placedOrders.total})` },
  { id: 'unplaced-orders', name: `Unplaced orders (${unplacedOrders.total})` },
  { id: 'reports', name: 'Reports' }
])
const { activeTab, sections: resellerSections } = useRouteTabs(
  resellerTabs,
  computed(() => `/resellers/${resellerId.value}`)
)

const today = new Date()
// Default to the last 30 days, matching the resellers list page.
const defaultRangeStart = new Date(today)
defaultRangeStart.setDate(today.getDate() - 30)
const dateRange = ref({ start: defaultRangeStart, end: today })
const calendarRange = ref({ start: dateRange.value.start, end: dateRange.value.end })

const formatRangeDate = date => {
  const opts = { month: 'short', day: 'numeric' }
  if (date.getFullYear() !== today.getFullYear()) opts.year = 'numeric'
  return date.toLocaleDateString(undefined, opts)
}
const rangeLabel = computed(() => `${formatRangeDate(dateRange.value.start)} - ${formatRangeDate(dateRange.value.end)}`)

const onCalendarRangeSelect = (value, close) => {
  if (value?.start && value?.end) {
    dateRange.value = { start: value.start, end: value.end }
  }
  close()
}

const addCustomerOpen = ref(false)
const addCustomerError = ref('')
const addingCustomer = ref(false)
const reportBottle = ref(null)
const customerPhone = usePhoneInput(addCustomerOpen)
const customerPhoneBox = customerPhone.container

watch(addCustomerOpen, () => {
  addCustomerError.value = ''
})

const sourceLabel = source =>
  ({ reseller_created: 'reseller (created)', reseller_claimed: 'reseller (claimed)', merchant_assigned: 'you' })[
    source
  ] || source

const formatDate = iso => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '')

const formatCurrency = value =>
  `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const totalCartValue = computed(() =>
  reportRows.value.reduce((sum, r) => sum + Number(r.attributes.owned_cart_value || 0), 0)
)

const chartData = computed(() => {
  if (!reportRows.value.length) return null
  // The report is already scoped to this reseller_id, so each row is one day;
  // sort chronologically so the bars read left to right in date order.
  const sorted = [...reportRows.value].sort((a, b) => (a.attributes.day < b.attributes.day ? -1 : 1))
  const labels = sorted.map(r => formatDate(serverDayToDateObject(r.attributes.day) || r.attributes.day))
  const barSpec = color => ({
    backgroundColor: color,
    borderRadius: 4,
    categoryPercentage: 0.6,
    barPercentage: 0.9
  })
  return {
    labels,
    datasets: [
      {
        label: 'Cart value',
        data: sorted.map(r => Number(r.attributes.owned_cart_value || 0)),
        ...barSpec(SERIES_CART_VALUE)
      }
    ]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    title: { display: false },
    tooltip: {
      padding: 10,
      cornerRadius: 6,
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      backgroundColor: '#111827',
      usePointStyle: true,
      callbacks: {
        label: ctx =>
          `${ctx.dataset.label}: $${Number(ctx.parsed.y || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: AXIS_INK }
    },
    y: {
      beginAtZero: true,
      grid: { color: GRIDLINE },
      border: { display: false },
      ticks: {
        color: AXIS_INK,
        callback: value => `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
      }
    }
  }
}

const fetchReseller = async () => {
  const id = resellerId.value
  const res = await api.call('GET', `resellers/${id}`)
  if (isCurrent(id)) reseller.value = res?.data?.data ?? null
}

const onResellerUpdated = updated => {
  reseller.value = updated
}

const fetchCustomers = async () => {
  const id = resellerId.value
  const params = new URLSearchParams({ limit: pageSize, page: customersPage.value })
  const res = await api.call('GET', `resellers/${id}/customers?${params.toString()}`)
  if (!isCurrent(id)) return
  customers.value = res?.data?.data ?? []
  customersTotal.value = res?.data?.total ?? customers.value.length
}

const fetchOrders = async list => {
  const id = resellerId.value
  const params = new URLSearchParams({
    included: 'checkout,conversation_user_pair,bottle_payment',
    status: list.status,
    beginning_date: dateObjectToServerDay(dateRange.value.start),
    ending_date: dateObjectToServerDay(dateRange.value.end),
    limit: pageSize,
    page: list.page
  })
  const res = await api.call('GET', `resellers/${id}/bottles?${params.toString()}`)
  if (!isCurrent(id)) return
  list.rows = res?.data?.data ?? []
  list.total = res?.data?.total ?? list.rows.length
}

const fetchAllOrders = () => Promise.all([placedOrders, unplacedOrders].map(fetchOrders))

const changeOrdersPage = (list, page) => {
  list.page = page
  return fetchOrders(list)
}

const fetchReport = async () => {
  const id = resellerId.value
  const params = new URLSearchParams({
    beginning_date: dateObjectToServerDay(dateRange.value.start),
    ending_date: dateObjectToServerDay(dateRange.value.end),
    reseller_id: id
  })
  const res = await api.call('GET', `data_downloads/reports/reseller_revenue_report?${params.toString()}`)
  if (isCurrent(id)) reportRows.value = res?.data?.data ?? []
}

const loadAll = async () => {
  await Promise.all([fetchReseller(), fetchCustomers(), fetchAllOrders(), fetchReport()])
}

const load = async () => {
  const id = resellerId.value
  loading.value = true
  try {
    await loadAll()
  } finally {
    if (isCurrent(id)) loading.value = false
  }
}

const exportDateRange = computed(() => ({
  beginning_date: dateObjectToServerDay(dateRange.value.start),
  ending_date: dateObjectToServerDay(dateRange.value.end),
  reseller_id: resellerId.value
}))

watch(dateRange, () => {
  placedOrders.page = 0
  unplacedOrders.page = 0
  return Promise.all([fetchReport(), fetchAllOrders()])
})

const customersPrev = () => {
  if (customersPage.value === 0) return
  customersPage.value--
  fetchCustomers()
}

const customersNext = () => {
  if (customersPage.value >= customersPageCount.value - 1) return
  customersPage.value++
  fetchCustomers()
}

const toggleActive = async () => {
  toggling.value = true
  try {
    const res = await api.call('PUT', `resellers/${resellerId.value}`, {
      is_active: !reseller.value.attributes.is_active
    })
    reseller.value = res?.data?.data ?? reseller.value
  } finally {
    toggling.value = false
  }
}

// BOT-1546: owned customers take the invoice journey when this is on.
const togglingInvoiceOnly = ref(false)
const toggleInvoiceOnly = async value => {
  togglingInvoiceOnly.value = true
  try {
    const res = await api.call('PUT', `resellers/${resellerId.value}`, { invoice_only: value })
    reseller.value = res?.data?.data ?? reseller.value
  } finally {
    togglingInvoiceOnly.value = false
  }
}

const addCustomer = async () => {
  if (!customerPhone.valid.value || addingCustomer.value) return
  addCustomerError.value = ''
  addingCustomer.value = true
  try {
    await api.call('POST', `resellers/${resellerId.value}/customers`, customerPhone.payload())
    addCustomerOpen.value = false
    await fetchCustomers()
  } catch (e) {
    addCustomerError.value = e?.response?.data?.errors?.[0] || 'Could not assign customer.'
  } finally {
    addingCustomer.value = false
  }
}

const removeFromReseller = async () => {
  if (!removeTarget.value || removing.value) return
  removing.value = true
  try {
    // { delete: false } keeps api.call from committing DESTROY_OBJECT into the
    // store, which would corrupt unrelated cached records (known gotcha).
    await api.call('DELETE', `resellers/${resellerId.value}/customers/${removeTarget.value.id}`, {}, { delete: false })
    removeTarget.value = null
    // Step back a page when the removal empties the current one.
    if (customers.value.length === 1 && customersPage.value > 0) customersPage.value--
    await loadAll()
  } finally {
    removing.value = false
  }
}

onMounted(load)

onBeforeRouteUpdate((to, from) => {
  if (to.params.id === from.params.id) return
  resellerId.value = to.params.id
  reseller.value = null
  customersPage.value = 0
  placedOrders.page = 0
  unplacedOrders.page = 0
  load()
})

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(SecondaryTop, {
        text: reseller.value ? _unref(resellerName)(reseller.value) : 'Reseller',
        description: reseller.value?.attributes.phone_number || undefined,
        sections: reseller.value && !loading.value ? _unref(resellerSections) : []
      }, {
        titleRight: _withCtx(() => [
          (reseller.value)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass([reseller.value.attributes.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600', "ml-2 shrink-0 px-2 py-0.5 rounded-full text-xs font-medium tracking-normal"])
              }, _toDisplayString(reseller.value.attributes.is_active ? 'Active' : 'Inactive'), 3))
            : _createCommentVNode("", true)
        ]),
        right: _withCtx(() => [
          (reseller.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(NeueButton, {
                  type: reseller.value.attributes.is_active ? 'secondary' : 'primary',
                  loading: toggling.value,
                  onClick: toggleActive
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(reseller.value.attributes.is_active ? 'Deactivate' : 'Reactivate'), 1)
                  ]),
                  _: 1
                }, 8, ["type", "loading"]),
                _createVNode(NeueButton, {
                  onClick: _cache[0] || (_cache[0] = $event => (addCustomerOpen.value = true))
                }, {
                  default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                    _createTextVNode("Add customers", -1)
                  ]))]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["text", "description", "sections"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(NeueLoading, { loading: loading.value }, {
          default: _withCtx(() => [
            (reseller.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  (canInvoice.value)
                    ? (_openBlock(), _createBlock(NeueWell, { key: 0 }, {
                        default: _withCtx(() => [
                          _createVNode(NeueToggle, {
                            "model-value": reseller.value.attributes.invoice_only,
                            name: "Invoice only",
                            description: "Customers this reseller owns place orders without paying at checkout and receive a Stripe invoice, even on stores that take cards.",
                            disabled: !reseller.value.attributes.is_active || togglingInvoiceOnly.value,
                            onChange: toggleInvoiceOnly
                          }, null, 8, ["model-value", "disabled"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(NeueWell, null, {
                      default: _withCtx(() => [
                        _createElementVNode("p", _hoisted_4, "Cart value (" + _toDisplayString(rangeLabel.value) + ")", 1),
                        _createElementVNode("p", _hoisted_5, _toDisplayString(formatCurrency(totalCartValue.value)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(NeueWell, null, {
                      default: _withCtx(() => [
                        _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Orders placed", -1)),
                        _createElementVNode("p", _hoisted_6, _toDisplayString(reseller.value.attributes.orders_count), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(NeueWell, null, {
                      default: _withCtx(() => [
                        _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-sm text-gray-500" }, "Customers", -1)),
                        _createElementVNode("p", _hoisted_7, _toDisplayString(customersTotal.value), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _createVNode(NeueWell, null, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_8, [
                        _cache[15] || (_cache[15] = _createElementVNode("p", { class: "text-sm font-medium text-gray-700" }, "Cart value over time", -1)),
                        _createVNode(_unref(Popover), {
                          as: "div",
                          class: "relative inline-block text-left"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(PopoverButton), { class: "report-filter-pill inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 min-w-0" }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(CalendarDaysIcon), { class: "h-4 w-4 text-gray-500 shrink-0" }),
                                _createElementVNode("span", _hoisted_9, _toDisplayString(rangeLabel.value), 1),
                                _createVNode(_unref(ChevronDownIcon), {
                                  class: "h-4 w-4 text-gray-500 shrink-0 ml-0.5",
                                  "aria-hidden": "true"
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_unref(PopoverPanel), { class: "absolute right-0 mt-2 z-50 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-4" }, {
                              default: _withCtx(({ close }) => [
                                _createVNode(_unref(DatePicker), {
                                  modelValue: calendarRange.value,
                                  "onUpdate:modelValue": [
                                    _cache[1] || (_cache[1] = $event => ((calendarRange).value = $event)),
                                    value => onCalendarRangeSelect(value, close)
                                  ],
                                  modelModifiers: { range: true },
                                  mode: "date",
                                  "max-date": new Date()
                                }, null, 8, ["modelValue", "max-date", "onUpdate:modelValue"])
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _createElementVNode("div", _hoisted_10, [
                        (chartData.value)
                          ? (_openBlock(), _createBlock(BarChart, {
                              key: 0,
                              "chart-data": chartData.value,
                              "chart-options": chartOptions
                            }, null, 8, ["chart-data"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 1
                  }),
                  _withDirectives(_createElementVNode("section", _hoisted_11, [
                    (!customers.value.length)
                      ? (_openBlock(), _createBlock(EmptyState, {
                          key: 0,
                          plural: "customers",
                          description: "No customers assigned yet."
                        }))
                      : (_openBlock(), _createElementBlock("ul", _hoisted_12, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customers.value, (row) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: row.id,
                              class: "py-3 flex items-center justify-between gap-3"
                            }, [
                              _createElementVNode("div", null, [
                                _createElementVNode("p", _hoisted_13, _toDisplayString(row.attributes.display_name), 1),
                                _createElementVNode("p", _hoisted_14, [
                                  _createTextVNode(_toDisplayString(row.attributes.phone_number) + " ", 1),
                                  _createElementVNode("span", _hoisted_15, "via " + _toDisplayString(sourceLabel(row.attributes.reseller_assignment_source)), 1)
                                ])
                              ]),
                              _createVNode(NeueButton, {
                                type: "secondary",
                                size: "sm",
                                onClick: $event => (removeTarget.value = row)
                              }, {
                                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                  _createTextVNode("Remove from reseller", -1)
                                ]))]),
                                _: 1
                              }, 8, ["onClick"])
                            ]))
                          }), 128))
                        ])),
                    (customersPageCount.value > 1)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _createVNode(NeueButton, {
                            type: "secondary",
                            size: "sm",
                            disabled: customersPage.value === 0,
                            onClick: customersPrev
                          }, {
                            default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                              _createTextVNode(" Previous ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled"]),
                          _createElementVNode("span", _hoisted_17, "Page " + _toDisplayString(customersPage.value + 1) + " of " + _toDisplayString(customersPageCount.value), 1),
                          _createVNode(NeueButton, {
                            type: "secondary",
                            size: "sm",
                            disabled: customersPage.value >= customersPageCount.value - 1,
                            onClick: customersNext
                          }, {
                            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                              _createTextVNode(" Next ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["disabled"])
                        ]))
                      : _createCommentVNode("", true)
                  ], 512), [
                    [_vShow, _unref(activeTab) === 'customers']
                  ]),
                  _withDirectives(_createElementVNode("section", null, [
                    _createVNode(ResellerMembers, {
                      "reseller-id": reseller.value.id,
                      members: reseller.value.attributes.members || [],
                      onUpdated: onResellerUpdated
                    }, null, 8, ["reseller-id", "members"])
                  ], 512), [
                    [_vShow, _unref(activeTab) === 'team-members']
                  ]),
                  _withDirectives(_createVNode(ResellerOrdersTable, {
                    orders: _unref(placedOrders),
                    "show-status": "",
                    "empty-description": "No orders placed in this date range.",
                    onDownload: _cache[2] || (_cache[2] = $event => (reportBottle.value = $event)),
                    onChangePage: _cache[3] || (_cache[3] = page => changeOrdersPage(_unref(placedOrders), page))
                  }, null, 8, ["orders"]), [
                    [_vShow, _unref(activeTab) === 'orders']
                  ]),
                  _withDirectives(_createVNode(ResellerOrdersTable, {
                    orders: _unref(unplacedOrders),
                    "empty-description": "No unplaced orders in this date range.",
                    onDownload: _cache[4] || (_cache[4] = $event => (reportBottle.value = $event)),
                    onChangePage: _cache[5] || (_cache[5] = page => changeOrdersPage(_unref(unplacedOrders), page))
                  }, null, 8, ["orders"]), [
                    [_vShow, _unref(activeTab) === 'unplaced-orders']
                  ]),
                  _withDirectives(_createElementVNode("section", null, [
                    _createVNode(ReportDownloadHistory, {
                      "report-type-filter": "bottle_by_range",
                      "simplified-report-type": "bottle_by_range",
                      "button-label": "Export orders",
                      "date-basis-note": "Reseller exports select orders by transaction date.",
                      "date-range": exportDateRange.value
                    }, null, 8, ["date-range"])
                  ], 512), [
                    [_vShow, _unref(activeTab) === 'reports']
                  ])
                ]))
              : (!loading.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_18, "Reseller not found."))
                : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["loading"])
      ]),
      (addCustomerOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-40 flex items-center justify-center bg-black/40",
            onClick: _cache[8] || (_cache[8] = _withModifiers($event => (addCustomerOpen.value = false), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_19, [
              _cache[21] || (_cache[21] = _createElementVNode("h2", { class: "text-lg font-semibold mb-4" }, "Add customers to reseller", -1)),
              _cache[22] || (_cache[22] = _createElementVNode("p", { class: "text-sm text-gray-500 mb-3" }, "Existing customers only.", -1)),
              _createElementVNode("div", {
                ref_key: "customerPhoneBox",
                ref: customerPhoneBox
              }, [
                _createVNode(InputField, {
                  modelValue: _unref(customerPhone).phone.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((_unref(customerPhone).phone.value) = $event)),
                  name: "Phone number",
                  type: "tel",
                  class: "w-full"
                }, null, 8, ["modelValue"])
              ], 512),
              (addCustomerError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(addCustomerError.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_21, [
                _createVNode(NeueButton, {
                  type: "secondary",
                  onClick: _cache[7] || (_cache[7] = $event => (addCustomerOpen.value = false))
                }, {
                  default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(NeueButton, {
                  type: _unref(customerPhone).valid.value ? 'primary' : 'inactive',
                  loading: addingCustomer.value,
                  onClick: addCustomer
                }, {
                  default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                    _createTextVNode(" Add customer ", -1)
                  ]))]),
                  _: 1
                }, 8, ["type", "loading"])
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      (removeTarget.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "fixed inset-0 z-40 flex items-center justify-center bg-black/40",
            onClick: _cache[10] || (_cache[10] = _withModifiers($event => (removeTarget.value = null), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_22, [
              _cache[25] || (_cache[25] = _createElementVNode("h2", { class: "text-lg font-semibold mb-2" }, "Remove from reseller", -1)),
              _createElementVNode("p", _hoisted_23, " Remove " + _toDisplayString(removeTarget.value.attributes.display_name) + " from this reseller? Order history and past attribution are kept, but the reseller will no longer see or place orders for this customer. ", 1),
              _createElementVNode("div", _hoisted_24, [
                _createVNode(NeueButton, {
                  type: "secondary",
                  onClick: _cache[9] || (_cache[9] = $event => (removeTarget.value = null))
                }, {
                  default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(NeueButton, {
                  loading: removing.value,
                  onClick: removeFromReseller
                }, {
                  default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                    _createTextVNode("Remove", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      (reportBottle.value)
        ? (_openBlock(), _createBlock(NewReportModal, {
            key: 2,
            "is-open": reportBottle.value != null,
            bottle: reportBottle.value,
            onCloseModal: _cache[11] || (_cache[11] = $event => (reportBottle.value = null))
          }, null, 8, ["is-open", "bottle"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

}